import { fromJS, Record } from 'immutable';
import { keys, truncate } from 'lodash';
import hourIntegerToString from '../utils/hours';
import { Base } from './base';
import Image from './image';
import moment from 'moment';

class Merchant extends Base(Record({
    id: null,
    _geoloc: {},
    _rankingInfo: {},
    country_code: '',
    contacts: [],
    hours: [],
    images: [],
    line1: '',
    line2: '',
    line3: '',
    locality: '',
    name: '',
    postal_code: '',
    region: '',
    tags: [],
    vendor: {},
    vendor_id: null
  })) {
    get distance() {
      if (!this._rankingInfo || keys(this._rankingInfo).length < 1) return false;

      return Math.round((this._rankingInfo.get('matchedGeoLocation').get('distance') / 1609.344) * 100) / 100;
    }

    get shortDescription() {
      return truncate(this.description, {
        length: 100,
        separator: ' '
      });
    }

    get followed() {
      return Boolean(this.state
        .entities
        .get('following')
        .get(parseInt(this.vendor_id, 10)));
    }

    $follow(userId) {
      if (!this.vendor_id) return Promise.reject('Invalid Merchant');

      const resource = 'vendors/' + this.vendor_id + '/relationships/followers';

      return this.api.post({
        resource: resource
      }, this.api.relationshipsPayload([{
        type: 'users',
        id: userId
      }]));
    }

    $unfollow(userId) {
      if (!this.vendor_id) return Promise.reject('Invalid Merchant');

      const resource = 'vendors/' + this.vendor_id + '/relationships/followers';

      return this.api.delete({
        resource: resource
      }, {
        body: JSON.stringify({
          data: [{
            type: 'users',
            id: userId
          }]
        })
      });
    }

    slugify() {
      return this.name.toLowerCase().replace(/([^a-z0-9]+)/gi, '-');
    }

    contact(type) {
      const contact = this.contacts.filter(c => c.get('type') === type);
      if (contact.size >= 1) return contact.first().get('text');
    }

    staticMap(mapboxKey, zoom = 12, size = '300x300') {
      const location = this._geoloc.toJS();
      return 'https://api.mapbox.com/styles/v1/bakedbean/ciy5v5uh3002x2snq96ztnzrs/static/' + location.lng + ',' + location.lat + ',' + zoom + '/' + size + '?access_token=' + mapboxKey;
    }

    profilePic() {
      const pic = this.images.find(i => i.get('label') === 'profile');

      return pic ? new Image(pic) : new Image({ url: '' });
    }

    bannerImages() {
      const omit = fromJS(['logo', 'posts', 'profile', 'profile-wide']);

      return this.images.filterNot(i => omit.includes(i.get('label')));
    }

    profilePicUrl(transformations, enableDefault = true) {
      const defaultUrl = 'https://res.cloudinary.com/localvore-today/image/upload/v1493998098/profile.png';

      const profilePic = this.images.find(i => i.get('label') === 'profile-wide') || this.images.find(i => i.get('label') === 'profile');
      const image = new Image({ url: profilePic ? profilePic.get('url') : defaultUrl });

      if(profilePic || enableDefault) {
        return image.withTransformations(transformations);
      }
      return null;
    }

    hours() {
      return this.state.entities.get('merchant').first().get('hours')
    }

    isOpenToday() {
      const today = moment().format('YYYY-MM-DD');
      const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
      const todayHours = this.todayHours;
      let open = todayHours.filter(h => moment().isBetween(moment(today + ' ' + hourIntegerToString(h.get('open'))), moment(today + ' ' + hourIntegerToString(h.get('close')))));

      if (open.size < 1) {
        open = todayHours.filter(h => moment().isBetween(moment(today + ' ' + hourIntegerToString(h.get('open'))), moment(tomorrow + ' ' + hourIntegerToString(h.get('close')))));
      }

      return open.size > 0;
    }

    get todayHours() {
      return this.hours().filter(h => h.get('day') === moment().format('dddd'));
    }

    get todayClose() {
      return this.isOpenToday() ? this.todayHours.first().get('close') : false;
    }
  }

export default Merchant;



// WEBPACK FOOTER //
// ./src/records/merchant.js